import { inject, Injectable } from '@angular/core';
import 'firebase/database';
import 'firebase/auth';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  updateEmail,
  updatePassword,
  deleteUser,
  sendPasswordResetEmail,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  User,
  updateProfile,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
  Functions,
} from 'firebase/functions';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { lastValueFrom, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserData } from '@core/authentication/user';
import { environment } from '@envs/environment';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalCheckEmailComponent } from '../../modal/components/modal-check-email/modal-check-email.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SendMailService } from '../send-mail/send-mail.service';
import { EmailService } from '../email/email.service';
import { serverTimestamp } from 'firebase/database';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly sendMail = inject(SendMailService);
  private readonly templateEmail = inject(EmailService);
  private readonly _router = inject(Router);
  user?: Observable<UserData>;
  app: any = initializeApp(environment.firebase);
  private db = getFirestore(this.app);
  userprofile?: UserData;
  public Token?: string;

  // Constante para aprovisionamiento técnico silencioso
  private readonly TEMP_PASS = 'Ms_Auth_Recovery_2026!#';

  constructor() {
    // Set default language to 'es' if not set
    const defaultLang = 'es';
    const storedLang = localStorage.getItem('language');

    // Set default language first
    this.translate.setDefaultLang(defaultLang);

    // Only use stored language if it exists and is valid
    if (storedLang === 'es' || storedLang === 'en') {
      this.translate.use(storedLang);
    } else {
      // Ensure we have a valid language in localStorage
      localStorage.setItem('language', defaultLang);
      this.translate.use(defaultLang);
    }
  }
  async loginWithGoogle(): Promise<User | null> {
    try {
      const auth = getAuth(this.app);
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/contacts.readonly');

      const result = await signInWithPopup(auth, provider);

      if (
        result.user.metadata.creationTime ===
        result.user.metadata.lastSignInTime
      ) {
        updateProfile(result.user, {
          photoURL: 'myservy',
        });
        this.eliminar();
      }

      if (result.user.email) {
        localStorage.setItem('emailprovider', result.user.email);
      }

      return result.user;
    } catch (error: any) {
      return null;
    }
  }

  registerUser(userRegister: UserData, pass: string): Promise<string> {
    const auth = getAuth(this.app);

    return createUserWithEmailAndPassword(auth, userRegister.email || '', pass)
      .then(async (userData) => {
        // Actualizar perfil y registrar datos del usuario
        await updateProfile(userData.user, { photoURL: 'myservy' });

        userRegister.uid = userData.user.uid;
        userRegister.googleid = userData.user.uid;
        userRegister.displayName = userRegister.displayFirstName;
        userRegister.FirstTime = false;

        this.RecordUserData(userRegister);

        // Registrar usuario en el backend
        const backendResponse = await lastValueFrom(
          this.registerUserBack(userRegister, userRegister.uid)
        );

        if (!backendResponse?.userId) {
          throw new Error('Error en el registro del backend');
        }

        // Preparar y enviar email de bienvenida
        const emailData = this.prepareEmailData(
          userRegister,
          backendResponse.email
        );
        await lastValueFrom(this.sendMail.SendMailGeneral(emailData));

        // Guardar datos en localStorage
        localStorage.setItem('uid', userData.user.uid);
        const token = await userData.user.getIdToken(true);
        localStorage.setItem('token', token);
        localStorage.setItem('uidCreat', userData.user.uid);
        localStorage.setItem('language', 'es');

        return userData.user.uid;
      })
      .catch((error) => {
        return this.handleRegistrationError(userRegister, null, error);
      });
  }

  private prepareEmailData(userRegister: UserData, email: string) {
    const language = localStorage.getItem('language') === 'en' ? 0 : 1;
    return {
      email,
      body: this.templateEmail.emailWelcomeMyservy(
        environment.appurl,
        `${environment.api}/img/MyservyLogo.png`,
        userRegister.displayFirstName || '',
        language ? 'ES/inicio-sesion' : 'EN/login',
        language
      ),
      Subject: language ? '¡Bienvenido a MyServy!' : 'Welcome to MyServy!',
    };
  }

  private handleRegistrationError(
    userRegister: UserData,
    userId: string | null,
    error: any
  ): Promise<never> {
    return lastValueFrom(this.DeleteUserFirebase(userRegister.email!)).then(
      () => {
        if (userId) {
          return lastValueFrom(this.deleteUserBack(userId)).then(() => {
            return Promise.reject(error?.error?.NativeErrorCode);
          });
        }
        return Promise.reject(error);
      }
    );
  }

  async registerUserInvite(userRegister: UserData, pass: string): Promise<any> {
    try {
      const auth = getAuth(this.app);
      const userData = await createUserWithEmailAndPassword(
        auth,
        userRegister.email!,
        pass
      );

      // Actualizar perfil del usuario
      await updateProfile(userData.user, { photoURL: 'myservy' });

      // Asignar datos al usuario
      Object.assign(userRegister, {
        uid: userData.user.uid,
        googleid: userData.user.uid,
        displayName: userRegister.displayFirstName,
        FirstTime: false,
      });

      this.RecordUserData(userRegister);

      if (!userRegister.uid)
        throw new Error('Error en el registro de firebase');
      // Registrar usuario en backend
      const backendResponse = await lastValueFrom(
        this.registerUserBack(userRegister, userRegister.uid)
      );

      if (!backendResponse.userId) {
        throw new Error('Error en el registro del backend');
      }

      return backendResponse;
    } catch (error) {
      await this.handleUserRegistrationError(userRegister.email!);
      throw error;
    }
  }

  // Método separado para manejar la eliminación del usuario en caso de error
  private async handleUserRegistrationError(email: string): Promise<void> {
    try {
      await lastValueFrom(this.DeleteUserFirebase(email));
    } catch (error) {
      console.error('Error al eliminar usuario en Firebase:', error);
    }
  }

  async registerUserGoogle(): Promise<{ email: string; new: boolean }> {
    try {
      const auth = getAuth(this.app);
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user = <any>result.user;

      user.updateProfile = 'myservy';

      const existingUser = await lastValueFrom(
        this.ExistUserForEmail(user.email)
      );

      if (existingUser) {
        return { email: user.email, new: true };
      }

      // Crear nuevo usuario
      const [firstName, ...lastNames] = user.displayName?.split(' ') ?? [];
      const userRegister: UserData = {
        uid: user.uid,
        googleid: user.uid,
        displayName: user.displayName,
        email: user.email,
        displayFirstName: firstName,
        displayLastName: lastNames.join(' '),
        TimeZone: 'UTC',
        AuthPoolId: '',
        userType: 0,
        Rol: 0,
        Language: localStorage.getItem('language') === 'es' ? 1 : 0,
      };

      this.RecordUserData(userRegister);

      if (!userRegister.uid)
        throw new Error('Error en el registro de firebase');
      // Registrar usuario en backend
      const backendResponse = await lastValueFrom(
        this.registerUserBack(userRegister, userRegister.uid)
      );

      if (!backendResponse?.userId) {
        throw new Error('Error en el registro del backend');
      }

      // Guardar datos en localStorage
      localStorage.setItem('uid', user.uid);
      const token = await user.getIdToken(true);
      localStorage.setItem('token', token);
      localStorage.setItem('uidCreat', user.uid);

      return { email: user.email, new: false };
    } catch (error) {
      console.error('Error en el registro con Google:', error);
      throw error;
    }
  }

  async registerUserFirebase(userRegisterEmail: string, pass: string) {
    try {
      const auth = getAuth(this.app);
      const userData = await createUserWithEmailAndPassword(
        auth,
        userRegisterEmail,
        pass
      );

      await updateProfile(userData.user, { photoURL: 'myservy' });

      return userData;
    } catch (error) {
      console.error('Error al registrar usuario en Firebase:', error);
      throw error;
    }
  }

  async loginEmail(email: string, pass: string) {
    try {
      const auth = getAuth(this.app);
      const userData = await signInWithEmailAndPassword(auth, email, pass);
      const { user } = userData;

      //Se comento logica legacy de photoURL creada por el Brayan y Armando Problemas
      //   if (user.photoURL === 'myservy') {
      localStorage.setItem('uid', user.uid);
      localStorage.setItem('email', email);
      localStorage.setItem('emailprovider', email);
      const token = await user.getIdToken(true);
      localStorage.setItem('token', token);
      return userData;
      //   }

      // if (user.photoURL === 'servicenter') {
      //   this._snackBar.open(
      //     'Error: Esta cuenta no pertenece a este apartado',
      //     '',
      //     {
      //       duration: 2000,
      //       panelClass: ['error'],
      //     }
      //   );
      //   throw new Error('Cuenta no válida para este apartado');
      // }

      // throw new Error('Cuenta con fotoURL desconocida');
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  public updateUser(userData: UserData) {
    const auth = getAuth(this.app);
    return updateProfile(auth.currentUser!, {
      displayName: userData.displayName,
      photoURL: userData.photoURL,
    });
  }

  private async RecordUserData(user: UserData) {
    const data: any = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      language: localStorage.getItem('language'),
      lastpasswordchange: serverTimestamp(),
      createat: serverTimestamp(),
    };

    try {
      await setDoc(doc(this.db, 'users', user.uid!), data);
    } catch (error) {
      console.error('Error writing document: ', error);
    }
  }

  logout() {
    const auth = getAuth(this.app);
    return signOut(auth);
  }

  async logoutAndRedirect() {
    try {
      await this.logout();
      localStorage.clear();
      localStorage.setItem('CerrarSesion', 'true');
      const lang = localStorage.getItem('language') ?? 'es';
      const linkLogin = lang === 'es' ? 'ES/inicio-sesion' : 'EN/login';
      this._router.navigate([linkLogin]);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  }

  eliminar() {
    const auth = getAuth(this.app);
    return deleteUser(auth.currentUser!);
  }

  resetPassword(email: string): Promise<any> {
    const auth = getAuth(this.app);
    let actionOptions = {
      url: environment.appurl,
    };
    return sendPasswordResetEmail(auth, email, actionOptions);
  }

  async customResetPassword(email: string, language: string, logoUrl?: string, primaryColor?: string): Promise<any> {
    try {
      const functions = this.getFunctionsInstance();
      const generateCustomResetLink = httpsCallable(functions, 'generateCustomResetLink');
      // La Cloud Function genera el enlace, compone el correo y lo envía.
      // El enlace nunca llega al navegador.
      const result: any = await generateCustomResetLink({
        email,
        continueUrl: environment.appurl,
        language,
        logoUrl: logoUrl || this.resolveLogoUrl(),
        primaryColor: primaryColor || this.resolvePrimaryColor(),
        providerAccountId: localStorage.getItem('accountid') || '',
      });

      return result.data;
    } catch (error) {
      console.error('Error in custom reset password flow:', error);
      throw error;
    }
  }

  async customResetPasswordServiceCenter(
    email: string,
    language: string,
    logoUrl?: string,
    primaryColor?: string,
    copyToEmails: string[] = [],
    customerName?: string
  ): Promise<any> {
    try {
      const functions = this.getFunctionsInstance();
      const generateCustomResetLink = httpsCallable(functions, 'generateCustomResetLink');

      const lang = language || localStorage.getItem('language') || 'es';
      const loginRoute = lang === '1' || lang.toLowerCase() === 'es' ? 'ES/inicio-sesion' : 'EN/login';
      const slug = localStorage.getItem('slug') || '';
      const appUrl = `${environment.urlservicecenter}/${loginRoute}/${slug}`;

      // La Cloud Function se encarga también de las copias.
      const result: any = await generateCustomResetLink({
        email,
        continueUrl: appUrl,
        language: lang,
        logoUrl: logoUrl || this.resolveLogoUrl(),
        primaryColor: primaryColor || this.resolvePrimaryColor(),
        customerName,
        providerAccountId: localStorage.getItem('accountid') || '',
        copyToEmails,
      });

      return result.data;
    } catch (error) {
      console.error('Error in Service Center reset password flow:', error);
      throw error;
    }
  }

  /**
   * Instancia de Cloud Functions, memoizada porque connectFunctionsEmulator
   * solo puede aplicarse una vez sobre la misma instancia.
   *
   * Con environment.useFunctionsEmulator en true y sirviendo desde localhost,
   * apunta al emulador (firebase.json: puerto 5001) en lugar del proyecto
   * desplegado. La doble condición evita que un flag mal commiteado desvíe
   * tráfico de recuperación de contraseña en un entorno real.
   */
  private functionsInstance?: Functions;

  private getFunctionsInstance(): Functions {
    if (!this.functionsInstance) {
      const instance = getFunctions(this.app, 'us-central1');
      const servedFromLocalhost =
        typeof location !== 'undefined' &&
        (location.hostname === 'localhost' || location.hostname === '127.0.0.1');

      if (environment.useFunctionsEmulator && servedFromLocalhost) {
        connectFunctionsEmulator(instance, '127.0.0.1', 5001);
        console.info('[Auth] Cloud Functions apuntando al emulador local (127.0.0.1:5001)');
      }

      this.functionsInstance = instance;
    }
    return this.functionsInstance;
  }

  private resolveLogoUrl(): string {
    const logoPath = localStorage.getItem('urlIL');
    if (logoPath) {
      return logoPath.startsWith('http')
        ? logoPath
        : environment.api + (logoPath.startsWith('/') ? logoPath : '/' + logoPath);
    }
    return environment.appurl + '/pictures/ServlyLogo.png';
  }

  private resolvePrimaryColor(): string {
    const storedColor = localStorage.getItem('primary');
    return storedColor ? `#${storedColor}` : '#EC0863';
  }

  async changePassword(
    email: string,
    oldPassword: string,
    newPassword: string
  ) {
    try {
      const auth = getAuth(this.app);

      // Autenticación con la contraseña antigua
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        oldPassword
      );

      // Cambio de contraseña
      await updatePassword(userCredential.user, newPassword);

      return { success: true, message: 'Contraseña actualizada correctamente' };
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error);
      throw error; // Mantiene el error para manejarlo fuera
    }
  }

  async changeEmail(
    ResetEmail: string,
    oldEmail: string,
    passwordEmail: string
  ) {
    try {
      const auth = getAuth(this.app);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        oldEmail,
        passwordEmail
      );
      updateEmail(userCredential.user, ResetEmail);
    } catch (error) {
      console.error('Error al cambiar email:', error);
      throw error; // Mantiene el error para manejarlo fuera
    }
  }

  loginserver(googleUid: string) {
    const url = environment.api + '/Authentication/login';
    const LoginModel = {
      GoogleID: googleUid,
    };
    return this.http.post(url, LoginModel);
  }

  SaveNewInviserver(data: any) {
    const url = environment.api + '/Authentication/newinviteuser';
    return this.http.post(url, data);
  }

  registerUserBack(user: UserData, uid: string): Observable<any> {
    const data = {
      Email: user.email,
      FirstName: user.displayFirstName ?? user.displayName,
      LastName: user.displayLastName ?? '',
      ProfilePhotoUrl: '/img/user.png',
      TimeZone: user.TimeZone,
      Language: user.Language,
      AuthPoolId: user.AuthPoolId,
      Rol: user.Rol,
      Country: user.Country,
      FirstTime: 0,
      FirstTour: 0,
      usertype: user.userType,
      googleid: user.googleid,
    };
    const url = environment.api + '/api/v1/users/create-user';
    return this.http.post<any>(url, data);
  }

  GetUserProfile(id: string) {
    const url = environment.api + '/Authentication/user/' + id;
    return this.http.get(url);
  }

  ExistUser(uid: string) {
    const url = environment.api + '/Authentication/verification/' + uid;
    return this.http.get(url);
  }

  ExistUserForEmail(email: string) {
    const url = environment.api + '/api/v1/users/email/' + email;
    return this.http.get(url);
  }

  async getPermissionWorkspace(token: string) {
    try {
      const myWorkspaceId = localStorage.getItem('workspaceID');
      if (!myWorkspaceId) {
        throw new Error(
          'Workspace ID no encontrado en el almacenamiento local'
        );
      }

      const url = `${environment.api}/Permission/${myWorkspaceId}`;
      const httpOp = {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      };

      // Se utiliza 'await' con el observable para obtener los datos
      const result = await this.http.get(url, httpOp).toPromise();
      return result;
    } catch (err) {
      console.error('Error al obtener permisos del workspace:', err);
      throw err; // Re-lanzamos el error para que el llamador lo maneje
    }
  }

  async getPermissionProject(token: string) {
    try {
      const myProjectId = localStorage.getItem('ProyectId');
      if (!myProjectId) {
        throw new Error('Project ID no encontrado en el almacenamiento local');
      }

      const url = `${environment.api}/Permission/Project/${myProjectId}`;
      const httpOp = {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      };

      // Convertimos el Observable a una Promesa para usar await
      return await this.http.get(url, httpOp).toPromise();
    } catch (err) {
      console.error('Error al obtener permisos del proyecto:', err);
      throw err; // Re-lanzamos el error para que quien llame la función lo maneje
    }
  }

  deleteInvite(data: any) {
    const url = environment.api + '/Authentication/deleteuser';
    return this.http.post(url, data);
  }

  DeleteUserFirebase(email: string) {
    console.log('no se registro back');
    const url = environment.urlFuntionDeleteUser + email;
    return this.http.get(url);
  }

  openDialogCheckEmail(email: string) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.autoFocus = true;
    if (localStorage.getItem('mobileStyle') === 'true') {
      dialogConfig.panelClass = 'custom-modal-check-email-mobile';
    } else if (localStorage.getItem('mobileStyle') === 'false') {
      dialogConfig.panelClass = 'custom-modal-check-email';
    } else {
      dialogConfig.panelClass = 'custom-modal-check-email';
    }

    dialogConfig.data = {
      title: 'Attach File',
      mail: email,
      lang: localStorage.getItem('language'),
    };

    const dialogRef = this.dialog.open(ModalCheckEmailComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((data) => {});
  }

  deleteUserBack(userId: string) {
    const url = `${environment.api}/api/v1/users/hard-delete/${userId}`;
    return this.http.put(url, '');
  }

  getAccountAndServiceCenter(userId: string) {
    const url = `${environment.api}/Authentication/provider-account-and-service-center-by-userId/${userId}`;
    return this.http.get(url);
  }

  async checkIfUserExistsInFirebase(email: string): Promise<boolean> {
    try {
      const auth = getAuth(this.app);
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      // If signInMethods array has length > 0, user exists
      return signInMethods.length > 0;
    } catch (error: any) {
      // If error code is 'auth/invalid-email', the email format is invalid
      if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email format');
      }
      // For any other error, assume user doesn't exist
      return false;
    }
  }

  checkEmailExistence(email: string) {
    const url = `${environment.api}/api/v1/users/email-dos/${email}`;
    return this.http.get(url);
  }

  async provisionFirebaseUser(email: string): Promise<void> {
    try {
      const auth = getAuth(this.app);
      const userData = await createUserWithEmailAndPassword(
        auth,
        email,
        this.TEMP_PASS
      );
      await updateProfile(userData.user, { photoURL: 'myservy' });
    } catch (error) {
      console.error('Error durante el aprovisionamiento de Firebase: ', error);
      throw error;
    }
  }
}
